import React, { useEffect, useMemo, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import { Badge, Input, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const ALERT_TYPE_LABELS = {
  action_required: "Action requise",
  action_due_soon: "Echeance proche",
  action_overdue: "Action en retard",
  workflow_status_changed: "Changement workflow",
  risk_zone_stale: "Zone sans mise a jour",
  risk_zone_action_overdue: "Action zone en retard",
};

const SEVERITY_BADGE = {
  critical: { color: "danger", label: "Critique" },
  warning: { color: "warning", label: "Alerte" },
  info: { color: "info", label: "Info" },
  success: { color: "success", label: "Succes" },
};

const DEFAULT_TYPES = [
  "action_required",
  "action_due_soon",
  "action_overdue",
  "workflow_status_changed",
  "risk_zone_stale",
  "risk_zone_action_overdue",
];

const ESCALATION_LEVEL_LABELS = {
  assignee: "Operateur",
  hse_manager: "Responsable HSE",
  management: "Management",
  executive: "Direction",
};

const PRIORITY_BADGE = {
  high: { color: "danger", label: "Haute" },
  medium: { color: "warning", label: "Moyenne" },
  low: { color: "success", label: "Basse" },
};

const AlertsCenterPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, critical: 0, due_soon: 0, overdue: 0, critical_over_sla: 0, critical_sla_minutes: 60 });
  const [loading, setLoading] = useState(true);
  const [takingOwnershipIds, setTakingOwnershipIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [status, setStatus] = useState("unread");
  const [severity, setSeverity] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [prioritizeWithAi, setPrioritizeWithAi] = useState(true);
  const [priorityLevel, setPriorityLevel] = useState("all");

  const selectedTypes = useMemo(() => {
    if (typeFilter === "all") return DEFAULT_TYPES;
    return [typeFilter];
  }, [typeFilter]);

  useEffect(() => {
    loadAlerts();
  }, [currentPage, status, severity, typeFilter, searchText, prioritizeWithAi, priorityLevel]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.getAlertStats();
      setStats(response || {});
    } catch (_) {
      // Ne pas bloquer l'écran si stats indisponibles
    }
  };

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemPerPage,
        status,
        severity,
        types: selectedTypes.join(","),
        search: searchText || undefined,
      };

      if (prioritizeWithAi) {
        params.priority_level = priorityLevel;
      }

      const response = prioritizeWithAi ? await api.getPrioritizedAlerts(params) : await api.getAlerts(params);

      const list = response?.data || [];
      setAlerts(Array.isArray(list) ? list : []);
      setTotalItems(response?.total || list.length || 0);
    } catch (error) {
      toast.error(error?.message || "Erreur lors du chargement des alertes");
      setAlerts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const onMarkAsRead = async (id) => {
    try {
      await api.markNotificationAsRead(id);
      toast.success("Alerte marquee comme lue");
      loadAlerts();
      loadStats();
    } catch (error) {
      toast.error(error?.message || "Erreur lors de la mise a jour");
    }
  };

  const onMarkAllAsRead = async () => {
    try {
      await api.markAllNotificationsAsRead();
      toast.success("Toutes les alertes ont ete marquees comme lues");
      loadAlerts();
      loadStats();
    } catch (error) {
      toast.error(error?.message || "Erreur lors de la mise a jour");
    }
  };

  const onTakeOwnership = async (alert) => {
    if (!alert?.id) return;
    setTakingOwnershipIds((prev) => [...prev, alert.id]);
    try {
      await api.takeAlertOwnership(alert.id);
      toast.success("Alerte prise en charge avec succes");
      loadAlerts();
      loadStats();
    } catch (error) {
      toast.error(error?.message || "Impossible de prendre en charge cette alerte");
    } finally {
      setTakingOwnershipIds((prev) => prev.filter((id) => id !== alert.id));
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return "A l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <React.Fragment>
      <Head title="Centre d'alertes" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Centre d&apos;alertes</BlockTitle>
              <BlockDes className="text-soft">
                Alertes critiques et echeances actions correctives
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              {stats.unread > 0 && (
                <Button color="danger" onClick={onMarkAllAsRead}>
                  <Icon name="check-circle" />
                  <span>Tout marquer comme lu</span>
                </Button>
              )}
              <Button
                color={prioritizeWithAi ? "primary" : "light"}
                className={stats.unread > 0 ? "ms-2" : ""}
                onClick={() => {
                  setPrioritizeWithAi((prev) => !prev);
                  setCurrentPage(1);
                }}
              >
                <Icon name="star" />
                <span>{prioritizeWithAi ? "Priorite IA active" : "Activer priorite IA"}</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="row g-3 mb-4">
            <div className="col-sm-6 col-lg-2">
              <div className="card card-bordered">
                <div className="card-inner py-3">
                  <h6 className="title mb-1">Total alertes</h6>
                  <div className="amount">{stats.total || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-bordered">
                <div className="card-inner py-3">
                  <h6 className="title mb-1">Non lues</h6>
                  <div className="amount text-warning">{stats.unread || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-bordered">
                <div className="card-inner py-3">
                  <h6 className="title mb-1">Critiques</h6>
                  <div className="amount text-danger">{stats.critical || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-bordered">
                <div className="card-inner py-3">
                  <h6 className="title mb-1">En retard</h6>
                  <div className="amount text-danger">{stats.overdue || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-bordered">
                <div className="card-inner py-3">
                  <h6 className="title mb-1">SLA depasse</h6>
                  <div className="amount text-danger">{stats.critical_over_sla || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-bordered">
                <div className="card-inner py-3">
                  <h6 className="title mb-1">SLA critique</h6>
                  <div className="amount text-primary">{stats.critical_sla_minutes || 60} min</div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <div className="form-control-wrap">
                <div className="form-icon form-icon-left">
                  <Icon name="search" />
                </div>
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher une alerte..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-md-8 d-flex justify-content-md-end">
              <UncontrolledDropdown>
                <DropdownToggle tag="a" className="me-2 btn btn-white btn-dim btn-outline-light dropdown-toggle">
                  <span>{status === "all" ? "Tous statuts" : status === "unread" ? "Non lues" : "Lues"}</span>
                  <Icon name="chevron-right" className="dd-indc" />
                </DropdownToggle>
                <DropdownMenu end>
                  <ul className="link-list-opt no-bdr">
                    <li><DropdownItem tag="a" href="#all" onClick={(e) => { e.preventDefault(); setStatus("all"); setCurrentPage(1); }}>Tous statuts</DropdownItem></li>
                    <li><DropdownItem tag="a" href="#unread" onClick={(e) => { e.preventDefault(); setStatus("unread"); setCurrentPage(1); }}>Non lues</DropdownItem></li>
                    <li><DropdownItem tag="a" href="#read" onClick={(e) => { e.preventDefault(); setStatus("read"); setCurrentPage(1); }}>Lues</DropdownItem></li>
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>

              <UncontrolledDropdown>
                <DropdownToggle tag="a" className="me-2 btn btn-white btn-dim btn-outline-light dropdown-toggle">
                  <span>{severity === "all" ? "Toutes severites" : severity === "critical" ? "Critique" : severity === "warning" ? "Alerte" : "Info"}</span>
                  <Icon name="chevron-right" className="dd-indc" />
                </DropdownToggle>
                <DropdownMenu end>
                  <ul className="link-list-opt no-bdr">
                    <li><DropdownItem tag="a" href="#sev-all" onClick={(e) => { e.preventDefault(); setSeverity("all"); setCurrentPage(1); }}>Toutes severites</DropdownItem></li>
                    <li><DropdownItem tag="a" href="#sev-critical" onClick={(e) => { e.preventDefault(); setSeverity("critical"); setCurrentPage(1); }}>Critique</DropdownItem></li>
                    <li><DropdownItem tag="a" href="#sev-warning" onClick={(e) => { e.preventDefault(); setSeverity("warning"); setCurrentPage(1); }}>Alerte</DropdownItem></li>
                    <li><DropdownItem tag="a" href="#sev-info" onClick={(e) => { e.preventDefault(); setSeverity("info"); setCurrentPage(1); }}>Info</DropdownItem></li>
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>

              <UncontrolledDropdown>
                <DropdownToggle tag="a" className="btn btn-white btn-dim btn-outline-light dropdown-toggle">
                  <span>{typeFilter === "all" ? "Tous types" : ALERT_TYPE_LABELS[typeFilter] || typeFilter}</span>
                  <Icon name="chevron-right" className="dd-indc" />
                </DropdownToggle>
                <DropdownMenu end>
                  <ul className="link-list-opt no-bdr">
                    <li><DropdownItem tag="a" href="#type-all" onClick={(e) => { e.preventDefault(); setTypeFilter("all"); setCurrentPage(1); }}>Tous types</DropdownItem></li>
                    {DEFAULT_TYPES.map((type) => (
                      <li key={type}>
                        <DropdownItem tag="a" href={`#type-${type}`} onClick={(e) => { e.preventDefault(); setTypeFilter(type); setCurrentPage(1); }}>
                          {ALERT_TYPE_LABELS[type]}
                        </DropdownItem>
                      </li>
                    ))}
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>

              {prioritizeWithAi && (
                <UncontrolledDropdown>
                  <DropdownToggle tag="a" className="ms-2 btn btn-white btn-dim btn-outline-light dropdown-toggle">
                    <span>
                      {priorityLevel === "all"
                        ? "Toutes priorites IA"
                        : priorityLevel === "high"
                        ? "Priorite haute"
                        : priorityLevel === "medium"
                        ? "Priorite moyenne"
                        : "Priorite basse"}
                    </span>
                    <Icon name="chevron-right" className="dd-indc" />
                  </DropdownToggle>
                  <DropdownMenu end>
                    <ul className="link-list-opt no-bdr">
                      <li><DropdownItem tag="a" href="#prio-all" onClick={(e) => { e.preventDefault(); setPriorityLevel("all"); setCurrentPage(1); }}>Toutes priorites IA</DropdownItem></li>
                      <li><DropdownItem tag="a" href="#prio-high" onClick={(e) => { e.preventDefault(); setPriorityLevel("high"); setCurrentPage(1); }}>Priorite haute</DropdownItem></li>
                      <li><DropdownItem tag="a" href="#prio-medium" onClick={(e) => { e.preventDefault(); setPriorityLevel("medium"); setCurrentPage(1); }}>Priorite moyenne</DropdownItem></li>
                      <li><DropdownItem tag="a" href="#prio-low" onClick={(e) => { e.preventDefault(); setPriorityLevel("low"); setCurrentPage(1); }}>Priorite basse</DropdownItem></li>
                    </ul>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </div>
          </div>

          <DataTable className="card-stretch">
            <DataTableBody>
              <DataTableHead className="nk-tb-item nk-tb-head">
                <DataTableRow><span className="sub-text">Alerte</span></DataTableRow>
                <DataTableRow><span className="sub-text">Type</span></DataTableRow>
                <DataTableRow><span className="sub-text">Severite</span></DataTableRow>
                <DataTableRow><span className="sub-text">Priorite IA</span></DataTableRow>
                <DataTableRow><span className="sub-text">Niveau</span></DataTableRow>
                <DataTableRow><span className="sub-text">Date</span></DataTableRow>
                <DataTableRow><span className="sub-text">Statut</span></DataTableRow>
                <DataTableRow className="nk-tb-col-tools"></DataTableRow>
              </DataTableHead>

              {loading ? (
                <DataTableItem>
                  <DataTableRow>
                    <DataTableItem colSpan={8} className="text-center py-4">
                      <Spinner color="primary" />
                    </DataTableItem>
                  </DataTableRow>
                </DataTableItem>
              ) : alerts.length === 0 ? (
                <DataTableItem>
                  <DataTableRow>
                    <DataTableItem colSpan={8} className="text-center py-4 text-soft">
                      Aucune alerte trouvee
                    </DataTableItem>
                  </DataTableRow>
                </DataTableItem>
              ) : (
                alerts.map((alert) => {
                  const sev = SEVERITY_BADGE[alert.severity] || { color: "secondary", label: alert.severity || "N/A" };
                  return (
                    <DataTableItem key={alert.id} className={!alert.read_at ? "nk-tb-item-unread" : ""}>
                      <DataTableRow>
                        <div className="d-flex align-items-center">
                          <Icon name={alert.icon || "alert-circle"} className="me-2 text-danger" />
                          <div>
                            <span className="tb-lead">{alert.title || "Alerte"}</span>
                            <span className="tb-sub d-block text-soft">{alert.message || ""}</span>
                          </div>
                        </div>
                      </DataTableRow>
                      <DataTableRow>
                        <span>{ALERT_TYPE_LABELS[alert.type] || alert.type}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <Badge color={sev.color}>{sev.label}</Badge>
                      </DataTableRow>
                      <DataTableRow>
                        {alert?.priority ? (
                          <div>
                            <Badge color={(PRIORITY_BADGE[alert.priority.level] || PRIORITY_BADGE.low).color}>
                              {(PRIORITY_BADGE[alert.priority.level] || PRIORITY_BADGE.low).label}
                            </Badge>
                            <div className="tb-sub text-soft mt-1">
                              Score {alert.priority.score || 0}
                            </div>
                            <div className="tb-sub text-soft">
                              Impact metier {alert.priority.business_impact_score || 0}
                            </div>
                            {Array.isArray(alert.priority.top_factors) && alert.priority.top_factors.length > 0 && (
                              <div className="tb-sub mt-1">
                                {alert.priority.top_factors.slice(0, 2).join(" • ")}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-soft">-</span>
                        )}
                      </DataTableRow>
                      <DataTableRow>
                        {alert?.data?.escalation_level ? (
                          <Badge color="primary">
                            {ESCALATION_LEVEL_LABELS[alert.data.escalation_level] || alert.data.escalation_level}
                          </Badge>
                        ) : (
                          <span className="text-soft">-</span>
                        )}
                      </DataTableRow>
                      <DataTableRow>
                        <div>
                          <span className="tb-sub d-block">{formatTime(alert.created_at)}</span>
                          {alert?.priority?.recommended_action && (
                            <span className="tb-sub text-soft d-block mt-1">{alert.priority.recommended_action}</span>
                          )}
                        </div>
                      </DataTableRow>
                      <DataTableRow>
                        {alert.read_at ? <Badge color="success">Lue</Badge> : <Badge color="warning">Non lue</Badge>}
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <div className="d-flex gap-1 justify-content-end">
                          {alert?.data?.observation_id && (
                            <Button
                              color="danger"
                              size="sm"
                              disabled={takingOwnershipIds.includes(alert.id)}
                              onClick={() => onTakeOwnership(alert)}
                            >
                              {takingOwnershipIds.includes(alert.id) ? (
                                <>
                                  <Spinner size="sm" className="me-1" />
                                  En cours...
                                </>
                              ) : (
                                <>
                                  <Icon name="user-check" className="me-1" />
                                  Prendre en charge
                                </>
                              )}
                            </Button>
                          )}
                          {!alert.read_at && (
                            <Button color="light" size="sm" onClick={() => onMarkAsRead(alert.id)}>
                              <Icon name="check" />
                            </Button>
                          )}
                        </div>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              )}
            </DataTableBody>
            <div className="card-inner">
              {totalItems > 0 && (
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={totalItems}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              )}
            </div>
          </DataTable>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default AlertsCenterPage;
